import { upsertBrickFromEpic } from '../../services/bricks';
import { edgeFunctionData } from '../../services/edgeFunctionData';
import { listJiraEpics } from '../../services/jiraSync';
import { notify } from '../../store/feedback';
import { uid } from '../../utils/format';

// Clients avec les briques ajoutées à la phase donnée.
const addBricksToPhase = (clients, clientId, phaseId, briques) =>
  clients.map((c) =>
    c.id !== clientId
      ? c
      : {
          ...c,
          phases: c.phases.map((ph) =>
            ph.id !== phaseId ? ph : { ...ph, briques: [...ph.briques, ...briques] },
          ),
        },
  );

/* Epics Jira : liste, import comme briques, et file d'import après une conversion en projet. */
export default function jiraEpicsHandlers(deps, api) {
  const { epicImportQueue, jiraEpicsModal, setEpicImportQueue, update } = deps;
  const { setJiraEpicsLoading, setJiraEpicsModal } = deps;

  async function handleListJiraEpics(client, phase) {
    setJiraEpicsLoading(true);
    try {
      const data = await edgeFunctionData(await listJiraEpics(client, phase));
      const existingEpics = new Set((phase.briques || []).map((b) => b.jira_epic).filter(Boolean));
      const epics = data.epics || [];
      const selected = new Set(epics.map((e) => e.key).filter((k) => !existingEpics.has(k)));
      setJiraEpicsModal({ clientId: client.id, phaseId: phase.id, epics, selected });
    } catch (e) {
      notify('Erreur : ' + (e.message || 'Impossible de récupérer les Epics Jira'));
    } finally {
      setJiraEpicsLoading(false);
    }
  }

  // Passe à l'élément suivant de la file d'import d'Epics (post-conversion), ou ferme la modal si la file est vide
  async function advanceEpicQueue() {
    if (epicImportQueue.length) {
      const [next, ...rest] = epicImportQueue;
      setEpicImportQueue(rest);
      await openEpicImportForQueueItem(next);
    } else {
      setJiraEpicsModal(null);
    }
  }

  async function handleImportEpics() {
    if (!jiraEpicsModal) return;
    const { clientId, phaseId, epics, selected } = jiraEpicsModal;
    const toImport = epics.filter((e) => selected.has(e.key));
    if (toImport.length) {
      const newBriques = toImport.map((e) => ({
        id: uid(),
        nom: e.summary,
        jira_epic: e.key,
        joursVendus: e.joursVendus || 0,
        echeance: '',
      }));
      for (const b of newBriques) {
        // onConflict sur (phase_id, jira_epic), pas sur id : la découverte automatique (cron) peut
        // avoir déjà créé une brique pour ce même epic entre l'ouverture de cette modale et ce clic
        // — sans ça, on créerait un doublon qui fausse "Jours vendus" (déjà arrivé, cf. migration
        // 005_briques_dedup_epics.sql).
        await upsertBrickFromEpic(b, phaseId);
      }
      let newClients;
      update((s) => {
        newClients = addBricksToPhase(s.clients, clientId, phaseId, newBriques);
        return { ...s, clients: newClients };
      });
      if (newClients) api.syncJiraConfig(newClients);
    }
    await advanceEpicQueue();
  }

  // Ouvre la sélection des epics pour un élément de la file d'import (projet Jira d'une phase).
  // Démarre (ou enchaîne sur) l'import des Epics Jira pour une phase forfait_brique issue de la conversion
  async function openEpicImportForQueueItem(item) {
    await handleListJiraEpics(
      { id: item.clientId, jira_project_key: item.jiraKey },
      { id: item.phaseId, jira_project_key: item.jiraKey, briques: [] },
    );
  }

  return {
    handleListJiraEpics,
    advanceEpicQueue,
    handleImportEpics,
    openEpicImportForQueueItem,
  };
}
